/**
 * B2 · Stages 2–5 — CLASSROOM CHAT.
 *
 * Four flags govern what's visible here, and each one turns off its part
 * without touching the others:
 *   · `academy.classroom.chat`            — the whole panel.
 *   · `academy.classroom.chat.promote`    — pin and promote to the forum.
 *   · `academy.classroom.chat.reactions`  — the emoji row.
 *   · `academy.classroom.chat.mentions`   — the notice when typing `@name`
 *     (here it's only highlighted; the fan-out is decided by the server).
 *
 * Hiding the UI is a courtesy, NEVER access control: the write-path
 * (`db/sync.rs`) is in charge, and it silently discards —with 2xx, so as not
 * to freeze the client's queue— whatever it doesn't touch.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { EyeOff, Lock, MessageCircle, Pin, Send, Trash2 } from "@fleet/icons";
import { CHAT_REACTION_EMOJIS, groupMessagesByDay, summarizeReactions } from "@space/shared";
import type { CohortChatMessage } from "@space/powersync";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { Skeleton } from "@fleet/ui";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import {
  useChatActions,
  useCohortChatMessages,
  useCohortChatReactions,
  useMyChatRead,
} from "../../hooks/use-classroom-chat";
import { useCohortIdentities } from "../../hooks/use-org-identities";
import { Avatar } from "@/components/ui/avatar";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

/** Position of `HH:MM` within an ISO-8601 string (`2026-07-28T09:15:00Z`). */
const ISO_TIME_START = 11;
const ISO_TIME_END = 16;

/** How long muting a classroom lasts from the UI: one week. */
const MUTE_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Chat mutations can now throw (`patchRowVerified` → `LocalWriteLostError`);
 * several buttons below are `void actions.X(...)` fire-and-forget, so
 * without this `.catch` they'd be unhandled rejections.
 */
const catchChatError = (action: string) => (err: unknown) => {
  console.error(`[classroom-chat] ${action} failed:`, err);
};

/** Is this classroom muted right now? */
function isMuted(mutedUntil: string | null | undefined): boolean {
  return Boolean(mutedUntil) && Date.parse(mutedUntil ?? "") > Date.now();
}

export const ClassroomChat: Component<{
  cohortId: string;
  organizationId?: string | null;
  /** Does the viewer moderate this classroom? (UX only: the server is authoritative). */
  canModerate: boolean;
  /** Cohort lock: when closed, only moderators can write. */
  locked?: boolean;
}> = (props) => {
  const { t } = useI18n();
  const { userId } = useSession();
  const [body, setBody] = createSignal("");

  const messages = useCohortChatMessages(() => props.cohortId);
  const reactions = useCohortChatReactions(() => props.cohortId);
  const read = useMyChatRead(() => props.cohortId);
  const roster = useCohortIdentities(() => props.cohortId);
  const actions = useChatActions(
    () => props.cohortId,
    () => props.organizationId ?? undefined,
  );

  const promote = useFeatureEnabled("academy.classroom.chat.promote");
  const withReactions = useFeatureEnabled("academy.classroom.chat.reactions");

  const days = createMemo(() => groupMessagesByDay(messages.data ?? []));
  const canWrite = () => !props.locked || props.canModerate;

  /** Display name of the author (or tombstone if they deleted their account). */
  const authorName = (message: CohortChatMessage) => {
    if (!message.author_id) {
      return t(msg`Former member`);
    }
    return roster.identityOf(message.author_id)?.name ?? t(msg`Classmate`);
  };

  const send = async () => {
    const text = body().trim();
    if (!text) {
      return;
    }
    setBody("");
    await actions.send(text);
    // What I just wrote can't count as unread for myself.
    await actions.markRead();
  };

  return (
    <section class="space-y-3 rounded-2xl border border-border p-5" data-testid="classroom-chat">
      <div class="flex items-center justify-between gap-2">
        <h2 class="flex items-center gap-2 font-display text-lg font-bold">
          <MessageCircle class="h-5 w-5 text-accent" /> {t(msg`Classroom chat`)}
        </h2>
        <button
          type="button"
          data-testid="classroom-chat-mark-read"
          class="text-xs text-muted-foreground underline"
          onClick={() => void actions.markRead().catch(catchChatError("markRead"))}
        >
          {t(msg`Mark as read`)}
        </button>
      </div>

      <Show when={!messages.isLoading} fallback={<Skeleton class="h-24 w-full rounded-xl" />}>
        <Show
          when={(messages.data ?? []).length > 0}
          fallback={
            <p class="text-sm text-muted-foreground" data-testid="classroom-chat-empty">
              {t(msg`No messages yet. Say hello to your cohort.`)}
            </p>
          }
        >
          <ol class="space-y-3" data-testid="classroom-chat-messages">
            <For each={days()}>
              {(day) => (
                <li class="space-y-2">
                  <p class="text-center text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    {day.day}
                  </p>
                  <For each={day.messages}>
                    {(message) => {
                      const summary = () =>
                        summarizeReactions(reactions.data ?? [], message.id, userId() ?? "");
                      const mine = () => message.author_id === userId();
                      return (
                        <div
                          class="rounded-xl border border-border p-3"
                          data-testid="classroom-chat-message"
                          data-message-id={message.id}
                          data-hidden={message.hidden_at ? "true" : "false"}
                        >
                          <p class="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            {/* `image` arrives redacted (null) for those who
                                can't show their photo to third parties — rule zero. */}
                            <Avatar
                              userId={message.author_id}
                              name={authorName(message)}
                              image={roster.identityOf(message.author_id ?? undefined)?.image}
                              class="h-5 w-5 text-[9px]"
                              testId="classroom-chat-avatar"
                            />
                            <span class="font-semibold text-foreground">{authorName(message)}</span>
                            <span>
                              {(message.created_at ?? "").slice(ISO_TIME_START, ISO_TIME_END)}
                            </span>
                            <Show when={message.pinned_at}>
                              <Pin class="h-3 w-3 text-accent" aria-label={t(msg`Pinned`)} />
                            </Show>
                            <Show when={message.hidden_at}>
                              <span
                                class="inline-flex items-center gap-1 text-danger"
                                data-testid="classroom-chat-hidden-mark"
                              >
                                <EyeOff class="h-3 w-3" /> {t(msg`Hidden by a moderator`)}
                              </span>
                            </Show>
                            <Show when={message.promoted_thread_id}>
                              <span class="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                                {t(msg`Now a forum thread`)}
                              </span>
                            </Show>
                          </p>
                          <p class="mt-1 whitespace-pre-line text-sm">{message.body}</p>

                          <Show when={withReactions().enabled}>
                            <div
                              class="mt-2 flex flex-wrap gap-1"
                              data-testid="classroom-chat-reactions"
                            >
                              <For each={CHAT_REACTION_EMOJIS}>
                                {(emoji) => {
                                  const count = () =>
                                    summary().counts.find((c) => c.emoji === emoji)?.count ?? 0;
                                  return (
                                    <button
                                      type="button"
                                      data-testid="classroom-chat-react"
                                      data-emoji={emoji}
                                      data-mine={summary().mine === emoji ? "true" : "false"}
                                      class="rounded-full border border-border px-2 py-0.5 text-xs"
                                      classList={{ "border-accent": summary().mine === emoji }}
                                      onClick={() =>
                                        void actions
                                          .toggleReaction(message.id, emoji, summary().mine)
                                          .catch(catchChatError("toggleReaction"))
                                      }
                                    >
                                      {emoji}
                                      <Show when={count() > 0}> {count()}</Show>
                                    </button>
                                  );
                                }}
                              </For>
                            </div>
                          </Show>

                          <div class="mt-2 flex flex-wrap gap-3 text-xs">
                            <Show when={props.canModerate}>
                              <button
                                type="button"
                                data-testid="classroom-chat-hide"
                                class="text-muted-foreground underline"
                                onClick={() =>
                                  void actions
                                    .setHidden(message.id, !message.hidden_at)
                                    .catch(catchChatError("setHidden"))
                                }
                              >
                                {message.hidden_at ? t(msg`Restore`) : t(msg`Hide`)}
                              </button>
                            </Show>
                            <Show when={props.canModerate && promote().enabled}>
                              <button
                                type="button"
                                data-testid="classroom-chat-pin"
                                class="text-muted-foreground underline"
                                onClick={() =>
                                  void actions
                                    .setPinned(message.id, !message.pinned_at)
                                    .catch(catchChatError("setPinned"))
                                }
                              >
                                {message.pinned_at ? t(msg`Unpin`) : t(msg`Pin`)}
                              </button>
                            </Show>
                            <Show when={mine() || props.canModerate}>
                              <button
                                type="button"
                                data-testid="classroom-chat-delete"
                                class="inline-flex items-center gap-1 text-muted-foreground underline"
                                onClick={() =>
                                  void actions.remove(message.id).catch(catchChatError("remove"))
                                }
                              >
                                <Trash2 class="h-3 w-3" /> {t(msg`Delete`)}
                              </button>
                            </Show>
                          </div>
                        </div>
                      );
                    }}
                  </For>
                </li>
              )}
            </For>
          </ol>
        </Show>
      </Show>

      <Show
        when={canWrite()}
        fallback={
          <p
            class="flex items-center gap-1.5 text-xs text-muted-foreground"
            data-testid="classroom-chat-locked"
          >
            <Lock class="h-3 w-3" /> {t(msg`This chat is closed by the academy.`)}
          </p>
        }
      >
        <div class="flex gap-2">
          <textarea
            class={field}
            rows={2}
            data-testid="classroom-chat-input"
            value={body()}
            onInput={(e) => setBody(e.currentTarget.value)}
            placeholder={t(msg`Write to your cohort…`)}
          />
          <button
            type="button"
            data-testid="classroom-chat-send"
            onClick={() => void send()}
            disabled={!body().trim()}
            class="shrink-0 self-start rounded-xl bg-accent px-4 py-2 text-sm font-bold text-accent-foreground disabled:opacity-50"
            aria-label={t(msg`Send`)}
          >
            <Send class="h-4 w-4" />
          </button>
        </div>
      </Show>

      <div class="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        {/* Honest copy about the transport: chat travels via sync, so delivery
            takes a checkpoint. Promising instant delivery would be a lie. */}
        <span>{t(msg`Messages may take a few seconds to reach everyone.`)}</span>
        <button
          type="button"
          data-testid="classroom-chat-mute"
          class="underline"
          onClick={() => {
            const until = isMuted(read.data?.muted_until)
              ? null
              : new Date(Date.now() + MUTE_DURATION_MS).toISOString();
            void actions.setMuted(until).catch(catchChatError("setMuted"));
          }}
        >
          <Show when={isMuted(read.data?.muted_until)} fallback={t(msg`Mute this classroom`)}>
            {t(msg`Unmute this classroom`)}
          </Show>
        </button>
      </div>
    </section>
  );
};
